import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FlipBook from '../components/FlipBook';
import SquadCrown from '../components/SquadCrown';
import { useEventRecovery, useEventSession } from '../context/EventSessionProvider';
import ReconnectingScreen from '../components/ReconnectingScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Live Book Mode — full-screen flipbook with persistent QR code,
 * auto-advance on by default, no close button. Designed for a venue TV
 * or projector so guests can watch the book fill up in real time.
 *
 * Hosts can share `https://eventdrop.co/event/:slug/live-book` on any
 * screen; there's a `/memory-book-message` QR overlay so passers-by can
 * scan and add their own entry without leaving the room.
 *
 * NOTE: deliberately NO back button — this is an unattended venue
 * surface (projector / TV). Exit is by closing the browser.
 */
const LiveBookPage = () => {
  const { slug } = useParams();
  const { event: recoveredEvent, status: recoveryStatus, retry: retryRecovery } = useEventRecovery(slug);
  const { activeEvent } = useEventSession();
  const notFound = recoveryStatus === 'failed';
  const [eventName, setEventName] = useState('Event');
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (recoveryStatus === 'ready' && recoveredEvent?.event_id) {
      if (recoveredEvent.event_name) setEventName(recoveredEvent.event_name);
      setEvent(recoveredEvent);
    }
  }, [recoveryStatus, recoveredEvent]);

  // Try to keep the screen awake on supported devices — ideal for venue displays
  useEffect(() => {
    let sentinel = null;
    const request = async () => {
      try {
        if ('wakeLock' in navigator) {
          sentinel = await navigator.wakeLock.request('screen');
        }
      } catch { /* ignore */ }
    };
    const onVisibility = () => { if (document.visibilityState === 'visible') request(); };
    request();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      try { sentinel?.release?.(); } catch {}
    };
  }, []);

  if (notFound) {
    return <ReconnectingScreen status="failed" onRetry={retryRecovery} dashboardHref="/" slug={slug} activeEvent={activeEvent} />;
  }

  const qrUrl = `${window.location.origin}/event/${slug}/memory-book-message`;

  return (
    <div className="fixed inset-0 bg-black" data-testid="live-book-page">
      <FlipBook
        slug={slug}
        eventNameFallback={eventName}
        autoStart
        hideClose
        qrUrl={qrUrl}
      />
      {/* Squad Crown hijacks the venue TV for 15 seconds when the host
          crowns the winning squad — full-screen confetti + trophy drop. */}
      <SquadCrown event={event} />
    </div>
  );
};

export default LiveBookPage;
